import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { StatusChip } from '../components/app/StatusChip';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Download, RefreshCw, Plus, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function ExportsPage() {
  const { periods, activePeriod } = usePeriod();
  const [exports, setExports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(activePeriod?.id || '');
  const [confirmDialog, setConfirmDialog] = useState(false);

  const loadExports = async () => {
    setLoading(true);
    try {
      const params = selectedPeriod ? { period_id: selectedPeriod } : {};
      const res = await API.get('/exports', { params });
      setExports(res.data || []);
    } catch(e) { toast.error('Failed to load exports'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadExports();
    if (activePeriod) setSelectedPeriod(activePeriod.id);
  }, [activePeriod?.id]);

  useEffect(() => { loadExports(); }, [selectedPeriod]);

  const generateExport = async (force = false) => {
    if (!selectedPeriod) { toast.error('Select a period first'); return; }
    setGenerating(true);
    setConfirmDialog(false);
    try {
      const res = await API.post('/exports/generate', { period_id: selectedPeriod, force });
      toast.success(`Export generated: ${res.data.row_count} rows, checksum ${res.data.export_checksum?.slice(0,8)}…`);
      if (res.data.forced) toast.warning('Export was force-generated — some transactions were not yet approved.');
      loadExports();
    } catch(e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        const confirmed = window.confirm(
          `${detail.message}\n\nApproved: ${detail.approved} / Total: ${detail.total}\nUncoded: ${detail.uncoded}\n\nClick OK to force-export approved transactions only (unapproved will be skipped).`
        );
        if (confirmed) {
          setGenerating(false);
          await generateExport(true);
          return;
        }
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Export failed');
      }
    } finally { setGenerating(false); }
  };

  const download = async (exportId, fileType) => {
    try {
      const res = await API.get(`/exports/${exportId}/download/${fileType}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `export_${fileType}_${exportId.slice(-6)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch(e) { toast.error('Download failed'); }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-xl font-bold">Exports</h1>

      {/* Generate export */}
      <div className="bg-white rounded-lg border shadow-sm p-5">
        <h3 className="text-sm font-semibold mb-3">Generate Acumatica GL Export</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Statement Period</label>
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="h-8 text-sm w-52">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {periods.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">{p.label} <StatusChip status={p.status} size="xs" /></span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            onClick={() => setConfirmDialog(true)}
            disabled={!selectedPeriod || generating}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="exports-generate-button"
          >
            {generating ? <><RefreshCw size={13} className="animate-spin mr-1" /> Generating…</> : <><Plus size={13} className="mr-1" /> Generate Export</>}
          </Button>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Generates 3 files: <span className="font-medium">acumatica_export.csv</span> (import-ready), <span className="font-medium">export_errors.csv</span>, <span className="font-medium">recon_summary.csv</span>
        </div>
      </div>

      {/* Export history */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold">Export History</h3>
        </div>
        {loading ? (
          <div className="py-8 text-center"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></div>
        ) : exports.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">No exports generated yet</div>
        ) : (
          <table className="data-table" data-testid="exports-version-history-table">
            <thead><tr>
              <th>Period</th><th>Version</th><th>Rows</th><th>Errors</th><th>Total</th>
              <th>Checksum</th><th>Generated By</th><th>Generated At</th>
              <th className="text-right">Download</th>
            </tr></thead>
            <tbody>
              {exports.map(exp => (
                <tr key={exp.id}>
                  <td className="font-semibold">{exp.period}</td>
                  <td className="tabular-nums">v{exp.version}</td>
                  <td className="tabular-nums">{exp.row_count}</td>
                  <td className={`tabular-nums ${exp.error_count > 0 ? 'text-orange-600 font-semibold' : ''}`}>{exp.error_count}</td>
                  <td className="tabular-nums font-medium">{exp.total_exported?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                  <td className="font-mono text-xs text-slate-400">{exp.export_checksum?.slice(0,12)}…</td>
                  <td className="text-xs text-slate-500">{exp.operator}</td>
                  <td className="text-xs text-slate-400">{exp.generated_at ? new Date(exp.generated_at).toLocaleString() : ''}</td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end" data-testid="exports-download-pack-button">
                      <button onClick={() => download(exp.id, 'export')} title="Download GL export" className="p-1.5 rounded border hover:bg-slate-50 text-slate-600">
                        <CheckCircle2 size={13} className="text-teal-600" />
                      </button>
                      <button onClick={() => download(exp.id, 'errors')} title="Download errors" className="p-1.5 rounded border hover:bg-slate-50">
                        <AlertTriangle size={13} className="text-orange-500" />
                      </button>
                      <button onClick={() => download(exp.id, 'recon')} title="Download reconciliation" className="p-1.5 rounded border hover:bg-slate-50">
                        <FileText size={13} className="text-emerald-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Generate Export?</AlertDialogTitle>
            <AlertDialogDescription>
              This will generate a new Acumatica GL export pack for {periods.find(p => p.id === selectedPeriod)?.label}.
              All allocated transactions will be included.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={generateExport} className="bg-teal-600">Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
